// EOL/EOS Context - State management and API integration

import React, { createContext, useState, useCallback, useContext } from 'react';
import type { ReactNode } from 'react';
import { useAuth } from '../../../auth/AuthContext';
import { hasPermission } from '../../../auth/permissions';
import { eolEosApi } from '../api';
import type {
  EndOfLifeProduct,
  PaginatedResponse,
  SearchCriteria,
  ProductRelation,
  CreateProductRequest,
  UpdateProductRequest,
  CreateRelationRequest,
  VendorType,
  EolEosContextType,
} from '../types';

const EolEosContext = createContext<EolEosContextType | undefined>(undefined);

interface EolEosProviderProps {
  children: ReactNode;
}

export const EolEosProvider: React.FC<EolEosProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canRead = hasPermission(user, 'eoleos.view');
  const canWrite = hasPermission(user, 'eoleos.write');
  const canImport = hasPermission(user, 'eoleos.import');

  // Centralized error handler
  const handleError = useCallback((err: any, customMessage: string): null => {
    console.error(err);
    const errorMessage = err.response?.data?.message || err.message || customMessage;
    setError(errorMessage);
    return null;
  }, []);

  // Get products with pagination
  const getProducts = useCallback(async (
    pageNumber: number = 1,
    pageSize: number = 30
  ): Promise<PaginatedResponse<EndOfLifeProduct> | null> => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await eolEosApi.getProducts(pageNumber, pageSize);
      return result;
    } catch (err: any) {
      return handleError(err, 'Failed to get EOL products');
    } finally {
      setIsLoading(false);
    }
  }, [handleError]);

  // Get single product
  const getProduct = useCallback(async (id: number): Promise<EndOfLifeProduct | null> => {
    setIsLoading(true);
    setError(null);
    try {
      const product = await eolEosApi.getProduct(id);
      return product;
    } catch (err: any) {
      return handleError(err, `Failed to get product with ID ${id}`);
    } finally {
      setIsLoading(false);
    }
  }, [handleError]);

  // Get manufacturers
  const getManufacturers = useCallback(async (): Promise<string[] | null> => {
    setIsLoading(true);
    setError(null);
    try {
      const manufacturers = await eolEosApi.getManufacturers();
      return manufacturers;
    } catch (err: any) {
      return handleError(err, 'Failed to get manufacturers');
    } finally {
      setIsLoading(false);
    }
  }, [handleError]);

  // Search products
  const searchProducts = useCallback(async (
    criteria: SearchCriteria,
    pageNumber: number = 1,
    pageSize: number = 30
  ): Promise<PaginatedResponse<EndOfLifeProduct> | null> => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await eolEosApi.searchProducts(criteria, pageNumber, pageSize);
      return result;
    } catch (err: any) {
      return handleError(err, 'Failed to search EOL products');
    } finally {
      setIsLoading(false);
    }
  }, [handleError]);

  // Add product
  const addProduct = useCallback(async (
    product: CreateProductRequest
  ): Promise<EndOfLifeProduct | null> => {
    setIsLoading(true);
    setError(null);
    try {
      const newProduct = await eolEosApi.addProduct(product);
      return newProduct;
    } catch (err: any) {
      return handleError(err, 'Failed to add EOL product');
    } finally {
      setIsLoading(false);
    }
  }, [handleError]);

  // Update product
  const updateProduct = useCallback(async (
    id: number,
    product: UpdateProductRequest
  ): Promise<EndOfLifeProduct | null> => {
    setIsLoading(true);
    setError(null);
    try {
      const updatedProduct = await eolEosApi.updateProduct(id, product);
      return updatedProduct;
    } catch (err: any) {
      return handleError(err, `Failed to update product with ID ${id}`);
    } finally {
      setIsLoading(false);
    }
  }, [handleError]);

  // Delete product
  const deleteProduct = useCallback(async (id: number): Promise<boolean> => {
    setIsLoading(true);
    setError(null);
    try {
      await eolEosApi.deleteProduct(id);
      return true;
    } catch (err: any) {
      handleError(err, `Failed to delete product with ID ${id}`);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [handleError]);

  // Get product relations
  const getProductRelations = useCallback(async (
    productId: number
  ): Promise<ProductRelation[] | null> => {
    setIsLoading(true);
    setError(null);
    try {
      const relations = await eolEosApi.getProductRelations(productId);
      return relations;
    } catch (err: any) {
      return handleError(err, `Failed to get relations for product ${productId}`);
    } finally {
      setIsLoading(false);
    }
  }, [handleError]);

  // Add product relation
  const addProductRelation = useCallback(async (
    relation: CreateRelationRequest
  ): Promise<ProductRelation | null> => {
    setIsLoading(true);
    setError(null);
    try {
      const newRelation = await eolEosApi.addProductRelation(relation);
      return newRelation;
    } catch (err: any) {
      return handleError(err, 'Failed to add product relation');
    } finally {
      setIsLoading(false);
    }
  }, [handleError]);

  // Update product relation
  const updateProductRelation = useCallback(async (
    productId: number,
    mspVendor: string,
    mspProdId: string,
    relationType: string
  ): Promise<ProductRelation | null> => {
    setIsLoading(true);
    setError(null);
    try {
      const updatedRelation = await eolEosApi.updateProductRelation(
        productId,
        mspVendor,
        mspProdId,
        relationType
      );
      return updatedRelation;
    } catch (err: any) {
      return handleError(err, 'Failed to update product relation');
    } finally {
      setIsLoading(false);
    }
  }, [handleError]);

  // Delete product relation
  const deleteProductRelation = useCallback(async (
    productId: number,
    mspVendor: string,
    mspProdId: string
  ): Promise<boolean> => {
    setIsLoading(true);
    setError(null);
    try {
      await eolEosApi.deleteProductRelation(productId, mspVendor, mspProdId);
      return true;
    } catch (err: any) {
      handleError(err, 'Failed to delete product relation');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [handleError]);

  // Upload CSV file
  const uploadFile = useCallback(async (
    file: File,
    vendorType: VendorType,
    onProgress?: (percent: number) => void
  ): Promise<EndOfLifeProduct[] | null> => {
    setIsLoading(true);
    setError(null);
    try {
      const products = await eolEosApi.uploadFile(file, vendorType, onProgress);
      return products;
    } catch (err: any) {
      return handleError(err, 'Failed to upload file');
    } finally {
      setIsLoading(false);
    }
  }, [handleError]);

  // Save products (bulk)
  const saveProducts = useCallback(async (
    products: EndOfLifeProduct[]
  ): Promise<string | null> => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await eolEosApi.saveProducts(products);
      return result;
    } catch (err: any) {
      return handleError(err, 'Failed to save products');
    } finally {
      setIsLoading(false);
    }
  }, [handleError]);

  const value: EolEosContextType = {
    isLoading,
    error,
    canRead,
    canWrite,
    canImport,
    getProducts,
    getProduct,
    getManufacturers,
    searchProducts,
    addProduct,
    updateProduct,
    deleteProduct,
    getProductRelations,
    addProductRelation,
    updateProductRelation,
    deleteProductRelation,
    uploadFile,
    saveProducts,
  };

  return <EolEosContext.Provider value={value}>{children}</EolEosContext.Provider>;
};

// Custom hook to use the context
export const useEolEos = (): EolEosContextType => {
  const context = useContext(EolEosContext);
  if (!context) {
    throw new Error('useEolEos must be used within an EolEosProvider');
  }
  return context;
};
